import React, { useEffect, useRef, useState } from "react";
import { mapStyle } from "../../LiveDashboardModal/components/GeoBackdrop";
import { toLatLng } from "../../../../utils/geo";
import {
  DEFAULT_CENTRE,
  imageCorners,
  floorCorners,
  shiftRef,
  rotateRefToward,
  handleSite,
  hitTest,
  nudgeRef,
  turnRef,
  keyAction,
  scaleFrame,
  dragResize,
  resizeCursor,
  resizeHandles,
} from "../utils/geoAlign";
import { formatSize } from "../utils/sizeChange";
import {
  lngLatOf,
  overlayData,
  addOverlayLayers,
  keepOverlays,
  SOURCES,
  IMAGE_BELOW,
} from "../utils/geoAlignLayers";

const loadMaplibre = () =>
  Promise.all([
    import(/* webpackChunkName: "maplibre" */ "maplibre-gl"),
    import(
      /* webpackChunkName: "maplibre" */ "maplibre-gl/dist/maplibre-gl.css"
    ),
  ]).then(([mod]) => mod.default || mod);

// A map click on a site drawn smaller than this (px) also zooms in to it.
const TOO_SMALL_PX = 40;

const fitFloor = (map, floor, ref) => {
  // The container may have just been laid out (the view just opened).
  map.resize();
  const pts = floorCorners(floor, ref).map(lngLatOf);
  const lngs = pts.map((p) => p[0]);
  const lats = pts.map((p) => p[1]);
  map.fitBounds(
    [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ],
    { padding: 80, maxZoom: 19, duration: 0 },
  );
};

const onScreenPx = (map, floor, ref) => {
  const [a, b] = [
    [0, 0],
    [floor.width, floor.height],
  ].map((p) => map.project(lngLatOf(toLatLng(ref, ...p))));
  return Math.hypot(a.x - b.x, a.y - b.y);
};

const showImage = (map, { image, floor, siteRef, imageUrl, opacity }) => {
  const coordinates = imageCorners(image, floor, siteRef).map(lngLatOf);
  const src = map.getSource("site-image");
  if (src) {
    src.setCoordinates(coordinates);
    return;
  }
  map.addSource("site-image", { type: "image", url: imageUrl, coordinates });
  map.addLayer(
    {
      id: "site-image",
      type: "raster",
      source: "site-image",
      paint: { "raster-opacity": opacity, "raster-fade-duration": 0 },
    },
    IMAGE_BELOW,
  );
};

const dragLabel = {
  position: "absolute",
  padding: "1px 6px",
  borderRadius: 4,
  background: "rgba(0, 0, 0, 0.75)",
  color: "#fff",
  fontSize: 12,
  fontVariantNumeric: "tabular-nums",
  pointerEvents: "none",
  whiteSpace: "nowrap",
};

const cursorFor = (over, floor, ref) => {
  if (over === "rotate") return "crosshair";
  if (over === "move") return "move";
  return over ? resizeCursor(over, floor, ref.rotationDeg) : "";
};

/**
 * The map: draws siteRef at `floor` and the reference points, reports drags,
 * rotations, clicks and key nudges through onRef, and resizes (handles and
 * - =) through onFrame({ref, floor}). It never owns the reference. Keys act
 * only while the map canvas has focus, so inputs keep their keys.
 */
const GeoAlignMap = ({
  tileLayers,
  layerId,
  siteRef,
  floor,
  image,
  imageUrl,
  opacity,
  mode,
  stations,
  focus,
  onRef,
  onFrame,
  onMapClick,
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [ready, setReady] = useState(false);
  // What is being dragged: "rotate", a resize handle's kind, "move" or null.
  const [dragging, setDragging] = useState(null);
  const live = useRef({});
  live.current = { ref: siteRef, floor, mode, onRef, onFrame, onMapClick };

  useEffect(() => {
    let cancelled = false;
    loadMaplibre().then((maplibre) => {
      if (cancelled || !containerRef.current) return;
      const map = new maplibre.Map({
        container: containerRef.current,
        style: mapStyle(tileLayers, layerId),
        center: lngLatOf([DEFAULT_CENTRE.lat, DEFAULT_CENTRE.lng]),
        zoom: 7,
        maxZoom: 20,
        attributionControl: false,
        fadeDuration: 0,
        dragRotate: false,
        pitchWithRotate: false,
        // Arrows are 0.1 m nudges (onKeyDown below), not map pans.
        keyboard: false,
      });
      let drag = null;
      const grab = (point) =>
        hitTest({
          ref: live.current.ref,
          floor: live.current.floor,
          point,
          project: (p) => map.project(lngLatOf(p)),
        });
      map.on("load", () => {
        addOverlayLayers(map);
        setReady(true);
      });
      map.on("mousedown", (e) => {
        map.getCanvas().focus({ preventScroll: true });
        const { ref: r, floor: f, mode: m } = live.current;
        if (m !== "adjust" || !r) return;
        const point = [e.lngLat.lat, e.lngLat.lng];
        const kind = grab(point);
        if (!kind) return;
        e.preventDefault();
        map.dragPan.disable();
        drag = { kind, start: point, ref0: r, floor0: f };
        setDragging(kind);
      });
      map.on("mousemove", (e) => {
        const { ref: r, floor: f, mode: m } = live.current;
        const point = [e.lngLat.lat, e.lngLat.lng];
        if (!drag) {
          const over = m === "adjust" && r && grab(point);
          map.getCanvas().style.cursor = cursorFor(over, f, r);
          return;
        }
        const { kind, start, ref0, floor0 } = drag;
        if (kind === "rotate") live.current.onRef(rotateRefToward(ref0, f, point));
        else if (kind === "move") live.current.onRef(shiftRef(ref0, start, point));
        else {
          live.current.onFrame(
            dragResize({ ref: ref0, floor: floor0, kind, start, point }),
          );
        }
      });
      map.on("mouseup", () => {
        if (!drag) return;
        drag = null;
        setDragging(null);
        map.dragPan.enable();
      });
      map.on("click", (e) =>
        live.current.onMapClick([e.lngLat.lat, e.lngLat.lng]),
      );
      mapRef.current = map;
    });
    return () => {
      cancelled = true;
      mapRef.current?.remove();
      mapRef.current = null;
      setReady(false);
    };
    // The map is built once per open; tileLayers is fixed for a session.
  }, []);

  // 正射影像 / 電子地圖: swap the base raster, keep every overlay.
  const styled = useRef(layerId);
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || styled.current === layerId) return;
    styled.current = layerId;
    map.setStyle(mapStyle(tileLayers, layerId), {
      transformStyle: keepOverlays,
    });
  }, [ready, layerId, tileLayers]);

  // Draw the site and the reference points.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map) return;
    const data = overlayData({ ref: siteRef, floor, stations });
    SOURCES.forEach((id) => map.getSource(id)?.setData(data[id]));
    if (image && siteRef) {
      showImage(map, { image, floor, siteRef, imageUrl, opacity });
    }
    // opacity has its own effect; here it only seeds a new image layer.
  }, [ready, siteRef, floor, image, imageUrl, stations]);

  useEffect(() => {
    if (ready && mapRef.current.getLayer("site-image")) {
      mapRef.current.setPaintProperty("site-image", "raster-opacity", opacity);
    }
  }, [ready, opacity]);

  // Jump (open, paste): fit the floor, or centre on a point.
  // A `soft` focus (a map click) zooms only when the site is too small to see.
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || !map || !focus) return;
    if (focus.ref) {
      if (!focus.soft || onScreenPx(map, floor, focus.ref) < TOO_SMALL_PX) {
        fitFloor(map, floor, focus.ref);
      }
    } else {
      map.jumpTo({ center: lngLatOf([focus.lat, focus.lng]), zoom: 7 });
    }
  }, [ready, focus]);

  const onKeyDown = (e) => {
    const canvas = mapRef.current?.getCanvas();
    const action = siteRef && e.target === canvas && keyAction(e);
    if (!action) return;
    e.preventDefault();
    if (action.scale) onFrame(scaleFrame(siteRef, floor, action.scale));
    else if (action.nudge) onRef(nudgeRef(siteRef, ...action.nudge));
    else onRef(turnRef(siteRef, floor, action.turn));
  };

  // The live label sits by the handle being dragged: the angle or the size.
  const grabbed =
    dragging === "rotate"
      ? handleSite(floor)
      : resizeHandles(floor).find((h) => h.kind === dragging)?.site;
  const labelAt =
    grabbed && siteRef && mapRef.current
      ? mapRef.current.project(lngLatOf(toLatLng(siteRef, ...grabbed)))
      : null;

  return (
    <div
      style={{ position: "relative", width: "100%", height: "100%" }}
      onKeyDown={onKeyDown}
    >
      <div
        ref={containerRef}
        data-testid="geo-align-map"
        aria-label="地圖：方向鍵移動場域 0.1 m（Shift 1 m），[ ] 旋轉 0.1°（Shift 1°），- = 縮放 0.1 %（Shift 1 %）"
        style={{ width: "100%", height: "100%" }}
      />
      {labelAt && (
        <div
          data-testid={
            dragging === "rotate" ? "geo-rotate-label" : "geo-size-label"
          }
          style={{ ...dragLabel, left: labelAt.x + 14, top: labelAt.y - 28 }}
        >
          {dragging === "rotate"
            ? `${siteRef.rotationDeg.toFixed(1)}°`
            : `${formatSize(floor)} m`}
        </div>
      )}
    </div>
  );
};

export default GeoAlignMap;
